// src/pages/dashboard/projectPage/AgentPanel.jsx
import React, { Component } from 'react';
import { AppContext } from '../../../context/AppContext';
import { NotificationContext } from '../../../state/notifications/NotificationContext';
import { urlMap, getAuthHeaders } from '../../../utils';
import { AgentStepsChips, AgentStatusPanel, AgentControlButtons, AgentLogs, connectLogStream, disconnectLogStream, parseLogEvent } from '../../components/agent';
import PlotModal from './PlotModal';

const STEP_RESULT_CONFIG = {
  SummarizeAndExtractStep:    { modalType: 'plot', tab: 'summaries' },
  AnalyzePlotStep:            { modalType: 'plot', tab: 'plot' },
  StructuralAndThematicStep:  { modalType: 'plot', tab: 'structure' },
  PlanRewriteStep:            { modalType: 'plot', tab: 'rewritePlan' },
};

/**
 * AgentPanel
 *
 * Self-contained container for all agent execution logic.
 * Renders to the right of the chapters container on the project page.
 *
 * Props:
 *  - project: current project object
 *  - onCharactersExtracted: callback
 *  - onChaptersSummarized: callback
 *  - onProjectMetadataChanged: callback
 *  - notificationContext: NotificationContext instance (injected by wrapper)
 */
class AgentPanelBase extends Component {
  static contextType = AppContext;

  state = {
    agentRunning: false,
    agentPaused: false,
    agentStep: 0,
    agentDone: false,
    agentError: null,
    executionId: null,
    canResume: false,
    stepResults: [],
    menuData: null,
    menuLoading: true,
    atCheckpoint: false,
    nextStep: null,
    completedStep: null,
    selectedStep: null,
    currentlyExecutingStep: null,
    currentStep: null,
    agentLogs: [],
    logsExpanded: true,
    confirmRerun: false,
    pendingExtraBody: null,
    activeResultModal: null,
    resultModalInitialTab: 'plot',
    pendingStructuralIssuesCount: null,
  };

  agentTimer = null;
  agentDoneTimeout = null;
  logSource = null;

  componentDidMount() {
    this.fetchMenuData();
    this.fetchLatestExecution();
    this.refreshPendingStructuralIssuesCount();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.project?.id && this.props.project.id !== prevProps.project?.id) {
      this.fetchLatestExecution();
      this.refreshPendingStructuralIssuesCount();
    }
    // Recalculate gating when project metadata changes (e.g., after approving structural issues)
    // Use count comparison instead of reference equality to catch updates where array reference stays the same
    const prevCount = this.countPendingStructuralIssues(prevProps.project?.metadata?.structuralThematicIssues || []);
    const newCount = this.countPendingStructuralIssues(this.props.project?.metadata?.structuralThematicIssues || []);
    if (newCount !== prevCount) {
      console.log('[AgentPanel] pending issues count changed:', prevCount, '->', newCount);
      this.setState({ pendingStructuralIssuesCount: newCount }, () => {
        console.log('[AgentPanel] setState completed, new state:', this.state.pendingStructuralIssuesCount);
      });
    }
    // Connect/disconnect SSE log stream when executionId changes
    if (this.state.executionId !== prevState.executionId) {
      this.disconnectLogs();
      if (this.state.executionId) {
        this.setState({ agentLogs: [] });
        this.connectLogs(this.state.executionId);
      }
    }
  }

  componentWillUnmount() {
    if (this.agentTimer) {
      clearInterval(this.agentTimer);
      this.agentTimer = null;
    }
    this.disconnectLogs();
    if (this.agentDoneTimeout) {
      clearTimeout(this.agentDoneTimeout);
      this.agentDoneTimeout = null;
    }
  }

  /* ────────── SSE log stream ────────── */

  connectLogs = (executionId) => {
    this.logSource = connectLogStream(executionId, (event) => {
      this.setState(s => ({ agentLogs: [...s.agentLogs, event] }));
      parseLogEvent(event, {
        onStepStart: this.handleStepStart,
        onStepCompleted: this.handleStepCompleted,
        onCheckpointReached: this.handleCheckpointReached,
        onExecutionComplete: this.handleExecutionComplete,
      });
    });
  };

  disconnectLogs = () => {
    disconnectLogStream(this.logSource);
    this.logSource = null;
  };

  /* ────────── data fetching ────────── */

  fetchMenuData = async () => {
    try {
      const response = await fetch(urlMap.getAgentMenu(), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch menu data');
      const data = await response.json();
      this.setState({ menuData: data, menuLoading: false });
    } catch (error) {
      console.error('[AgentPanel] Error fetching menu data:', error);
      this.setState({ menuLoading: false });
    }
  };

  fetchLatestExecution = async () => {
    try {
      const project = this.props.project;
      if (!project?.id) return;

      const response = await fetch(urlMap.getLatestAgentExecution(project.id), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch latest execution');

      const data = await response.json();
      if (!data.execution) return;

      const execution = data.execution;
      console.log('[AgentPanel] Latest execution:', execution);

      if (execution.status === 'running') {
        this.setState({
          executionId: execution.id,
          agentRunning: true,
          agentPaused: false,
          stepResults: execution.stepsExecuted || [],
          currentStep: execution.currentStep,
        });
      } else if (execution.status === 'paused') {
        const lastStep = execution.stepsExecuted?.[execution.stepsExecuted.length - 1];
        this.setState({
          executionId: execution.id,
          agentRunning: false,
          agentPaused: true,
          atCheckpoint: true,
          completedStep: lastStep?.step,
          nextStep: execution.nextStep,
          canResume: true,
          stepResults: execution.stepsExecuted || [],
          currentStep: execution.currentStep,
        });
      } else if (execution.status === 'cancelled') {
        this.setState({
          executionId: execution.id,
          agentRunning: false,
          agentPaused: false,
          atCheckpoint: false,
          canResume: false,
          stepResults: execution.stepsExecuted || [],
          agentError: execution.error || 'Execution cancelled',
          currentStep: execution.currentStep,
        });
      } else if (execution.status === 'completed' || execution.status === 'failed') {
        this.setState({
          executionId: execution.id,
          stepResults: execution.stepsExecuted || [],
          agentError: execution.status === 'failed' ? execution.error : null,
          currentStep: execution.currentStep,
        });
      }
    } catch (error) {
      console.error('[AgentPanel] Error fetching latest execution:', error);
    }
  };

  countPendingStructuralIssues = (issues = []) => {
    if (!Array.isArray(issues)) return 0;
    const pending = issues.filter((issue) => {
      const status = String(issue?.status || 'pending').toLowerCase();
      return !['approved', 'dismissed', 'applied', 'done', 'resolved'].includes(status);
    });
    console.log('[AgentPanel] countPendingStructuralIssues:', {
      total: issues.length,
      pending: pending.length,
      issues: issues.map(i => ({ id: i.id, title: i.title, status: i.status })),
    });
    return pending.length;
  };

  refreshPendingStructuralIssuesCount = async () => {
    const projectId = this.props.project?.id;
    if (!projectId) return 0;

    try {
      const response = await fetch(urlMap.getStructuralThematicIssues(projectId), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const issues = await response.json();
      const pendingCount = this.countPendingStructuralIssues(Array.isArray(issues) ? issues : []);
      this.setState({ pendingStructuralIssuesCount: pendingCount });
      return pendingCount;
    } catch (error) {
      const fallback = this.countPendingStructuralIssues(this.props.project?.metadata?.structuralThematicIssues || []);
      this.setState({ pendingStructuralIssuesCount: fallback });
      return fallback;
    }
  };

  getPendingStructuralIssuesCount = () => {
    if (typeof this.state.pendingStructuralIssuesCount === 'number') {
      return this.state.pendingStructuralIssuesCount;
    }
    return this.countPendingStructuralIssues(this.props.project?.metadata?.structuralThematicIssues || []);
  };

  getEffectiveAgentSteps = () => {
    const steps = this.getAgentSteps();
    const rewritePlanLocked = this.getPendingStructuralIssuesCount() > 0;
    return steps.map((step) => {
      if (step.name !== 'PlanRewriteStep') return step;
      return {
        ...step,
        enabled: step.enabled !== false && !rewritePlanLocked,
      };
    });
  };

  /* ────────── helper: post-execution refresh ────────── */

  notifyStepCallbacks = (results) => {
    if (!results || results.length === 0) return;

    const hasCharacters = results.some(r => r.step === 'SummarizeAndExtractStep' && r.success);
    const hasSummaries = results.some(r => r.step === 'SummarizeAndExtractStep' && r.success);
    const hasPlot = results.some(r =>
      (r.step === 'AnalyzePlotStep' ||
       r.step === 'PlanRewriteStep' ||
       r.step === 'StructuralAndThematicStep') && r.success
    );

    if (hasCharacters) {
      console.log('[AgentPanel] Refreshing characters');
      this.props.onCharactersExtracted?.();
    }
    if (hasSummaries) {
      console.log('[AgentPanel] Refreshing chapters');
      this.props.onChaptersSummarized?.();
    }
    if (hasPlot) {
      console.log('[AgentPanel] Refreshing project metadata');
      this.props.onProjectMetadataChanged?.();
    }

    // Also store summaries in context
    const summarizeStep = results.find(r => r.step === 'SummarizeAndExtractStep');
    if (summarizeStep?.metadata?.summaries) {
      const project = this.props.project;
      const summariesData = summarizeStep.metadata.summaries.map(s => ({
        chapterId: s.chapter_id,
        summaryId: s.id,
        provider: s.provider,
        model: s.model,
        orderNo: s.order_no,
        success: true,
      }));
      this.context?.setChapterSummaries?.(project?.id, summariesData);
    }
  };

  notifyCheckpointCallbacks = (stepName) => {
    if (stepName === 'SummarizeAndExtractStep') this.props.onCharactersExtracted?.();
    if (stepName === 'SummarizeAndExtractStep') this.props.onChaptersSummarized?.();
    if (
      stepName === 'AnalyzePlotStep' ||
      stepName === 'PlanRewriteStep' ||
      stepName === 'StructuralAndThematicStep'
    ) {
      this.props.onProjectMetadataChanged?.();
    }

    if (stepName === 'StructuralAndThematicStep') {
      this.refreshPendingStructuralIssuesCount();
    }
  };

  handleStructuralIssuesUpdated = () => {
    // Refresh directly from API so checkpoint unblock is immediate after approve/dismiss.
    this.refreshPendingStructuralIssuesCount();
    this.props.onProjectMetadataChanged?.();
  };

  /* ────────── agent control methods ────────── */

  /**
   * Called externally (via ref) to trigger a run from a specific step
   * with optional extra body data (e.g. userComment).
   */
  triggerRunFromStep = (fromStep, extraData = {}) => {
    this.setState({ selectedStep: fromStep }, () => {
      this.startAgent(extraData, { forceRerun: true, fromStep, skipConfirmation: true });
    });
  };

  startAgent = async (extraBody = {}, options = {}) => {
    if (this.state.agentRunning) return;
    const project = this.props.project;
    if (!project?.id) return;

    const selectedStep = options.fromStep ?? this.state.selectedStep;
    const forceRerun = !!options.forceRerun;
    const skipConfirmation = !!options.skipConfirmation;
    const hasExistingData = this.getStepResultsWithProjectData().length > 0;

    // Only force-reruns should ask for destructive confirmation.
    if (forceRerun && !skipConfirmation && !this.state.confirmRerun && (selectedStep || hasExistingData)) {
      this.setState({ confirmRerun: true, pendingExtraBody: extraBody });
      return;
    }

    // Clear step results only for force reruns.
    let clearedResults = [];
    if (forceRerun && selectedStep) {
      // Remove only the selected step, keep all other steps' results
      clearedResults = this.state.stepResults.filter(r => r.step !== selectedStep);
    } else if (!forceRerun) {
      clearedResults = this.state.stepResults;
    }

    // Get first enabled step for immediate visual feedback
    const firstEnabledStep = this.getEffectiveAgentSteps().find(s => s.enabled !== false);

    this.setState({
      confirmRerun: false,
      pendingExtraBody: null,
      selectedStep: null,
      agentRunning: true,
      agentStep: 0,
      agentDone: false,
      agentError: null,
      stepResults: clearedResults,
      atCheckpoint: false,
      canResume: false,
      agentPaused: false,
      nextStep: null,
      completedStep: null,
      currentStep: null,
      currentlyExecutingStep: selectedStep || firstEnabledStep?.name || null,
    });

    this.agentTimer = setInterval(() => {
      this.setState(s => ({ agentStep: Math.min(s.agentStep, 0) }));
    }, 1000);

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const url = urlMap.executeAgent(project.id);
      const requestBody = { stage: 'project_stage', forceRerun, ...extraBody };

      if (selectedStep) {
        requestBody.fromStep = selectedStep;
        console.log(`[AgentPanel] Starting from step: ${selectedStep}`);
      }

      const response = await fetch(url, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok && response.status !== 202) {
        const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(error.message || error.error || 'Agent execution failed');
      }

      const result = await response.json();
      console.log('[AgentPanel] Execution started:', result);
      this.setState({ executionId: result.executionId });

      // 202 = running in background (SSE will drive updates)
      if (response.status === 202) return;

      // Sync response (200)
      if (result.paused) {
        this.clearTimer();
        const last = result.results[result.results.length - 1];
        this.setState({
          agentRunning: false, agentPaused: true, atCheckpoint: true,
          nextStep: result.nextStep, completedStep: last?.step,
          canResume: true, stepResults: result.results, agentError: null,
        });
        return;
      }

      if (result.success) {
        this.notifyStepCallbacks(result.results);
        this.clearTimer();
        this.setState({ agentDone: true, agentError: null, stepResults: result.results });
        this.autoReset();
      } else {
        throw new Error(result.error || 'Agent execution failed');
      }
    } catch (error) {
      console.error('[AgentPanel] Execution error:', error);
      this.clearTimer();
      this.setState({
        agentRunning: false, agentStep: 0, agentDone: false,
        agentError: error.message || 'Failed to run agent',
        canResume: !!this.state.executionId,
      });
    }
  };

  resumeAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;
    const project = this.props.project;
    if (!project?.id) return;

    this.setState({
      agentRunning: true, agentPaused: false,
      agentError: null, canResume: false, atCheckpoint: false,
    });
    this.agentTimer = setInterval(() => this.setState(s => ({ agentStep: Math.min(s.agentStep, 0) })), 1000);

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await fetch(urlMap.resumeAgent(executionId), {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ stage: 'project_stage' }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Resume failed');
      }
      const result = await response.json();

      if (result.paused) {
        this.clearTimer();
        const last = result.results[result.results.length - 1];
        this.setState({
          agentRunning: false, agentPaused: true, atCheckpoint: true,
          nextStep: result.nextStep, completedStep: last?.step,
          canResume: true, stepResults: result.results, agentError: null,
        });
        return;
      }
      if (result.success) {
        this.notifyStepCallbacks(result.results);
        this.clearTimer();
        this.setState({ agentDone: true, agentError: null, stepResults: result.results });
        this.autoReset();
      } else {
        throw new Error(result.error || 'Resume failed');
      }
    } catch (error) {
      console.error('[AgentPanel] Resume error:', error);
      this.clearTimer();
      this.setState({ agentRunning: false, agentError: error.message, canResume: true });
    }
  };

  continueAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;
    const project = this.props.project;
    if (!project?.id) return;

    this.setState({
      agentRunning: true, agentPaused: false,
      agentError: null, canResume: false, atCheckpoint: false,
    });
    this.agentTimer = setInterval(() => this.setState(s => ({ agentStep: Math.min(s.agentStep, 0) })), 1000);

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await fetch(urlMap.continueAgent(executionId), {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ stage: 'project_stage' }),
      });
      if (!response.ok && response.status !== 202) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Continue failed');
      }
      // 202 = running in background — SSE stream will deliver checkpoint/completion events
      if (response.status === 202) return;

      // Legacy sync 200 path (kept for safety)
      const result = await response.json();
      const prev = this.state.stepResults || [];
      const merged = [...prev];
      (result.results || []).forEach(nr => {
        if (!merged.find(pr => pr.step === nr.step)) merged.push(nr);
      });
      if (result.paused) {
        this.clearTimer();
        const last = result.results[result.results.length - 1];
        this.setState({
          agentRunning: false, agentPaused: true, atCheckpoint: true,
          nextStep: result.nextStep, completedStep: last?.step,
          canResume: true, stepResults: merged, agentError: null,
        });
        return;
      }
      if (result.success) {
        this.notifyStepCallbacks(result.results);
        this.clearTimer();
        this.setState({ agentDone: true, agentError: null, stepResults: merged, atCheckpoint: false });
        this.autoReset();
      } else {
        throw new Error(result.error || 'Continue failed');
      }
    } catch (error) {
      console.error('[AgentPanel] Continue error:', error);
      this.clearTimer();
      this.setState({ agentRunning: false, agentError: error.message, canResume: true, atCheckpoint: true });
    }
  };

  pauseAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;
    try {
      const response = await fetch(urlMap.pauseAgent(executionId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Pause failed');
      }
      this.clearTimer();
      this.setState({ agentRunning: false, agentPaused: true, canResume: true, agentError: null });
    } catch (error) {
      console.error('[AgentPanel] Pause error:', error);
      this.setState({ agentError: error.message });
    }
  };

  cancelAgent = async () => {
    const { executionId } = this.state;
    if (!executionId) return;

    try {
      const response = await fetch(urlMap.cancelAgent(executionId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err.message || err.error || 'Cancel failed');
      }

      this.clearTimer();
      this.setState({
        agentRunning: false,
        agentPaused: false,
        atCheckpoint: false,
        canResume: false,
        currentlyExecutingStep: null,
        agentError: 'Execution cancelled by user',
      });
    } catch (error) {
      console.error('[AgentPanel] Cancel error:', error);
      this.setState({ agentError: error.message || 'Cancel failed' });
    }
  };

  stopAgent = () => {
    this.clearTimer();
    if (this.agentDoneTimeout) { clearTimeout(this.agentDoneTimeout); this.agentDoneTimeout = null; }
    this.setState({
      agentRunning: false, agentPaused: false, agentStep: 0,
      agentDone: false, agentError: null, canResume: false,
      atCheckpoint: false, nextStep: null, completedStep: null,
    });
  };

  resetAndRun = () => {
    this.clearTimer();
    if (this.agentDoneTimeout) { clearTimeout(this.agentDoneTimeout); this.agentDoneTimeout = null; }
    this.setState({
      agentRunning: false, agentPaused: false, agentStep: 0,
      agentDone: false, agentError: null, canResume: false,
      atCheckpoint: false, nextStep: null, completedStep: null,
      selectedStep: null, stepResults: [], executionId: null,
      confirmRerun: false, pendingExtraBody: null,
    }, () => {
      this.startAgent({}, { forceRerun: true, skipConfirmation: true });
    });
  };

  handleRerunFromStep = (stepName) => {
    if (this.state.agentRunning || this.state.agentPaused) return;

    // Immediately clear completed state for this step and all subsequent steps
    const agentSteps = this.getAgentSteps();
    const fromIdx = agentSteps.findIndex(s => s.name === stepName);
    const clearedResults = fromIdx > 0
      ? this.state.stepResults.filter(r => {
          const rIdx = agentSteps.findIndex(s => s.name === r.step);
          return rIdx >= 0 && rIdx < fromIdx;
        })
      : [];

    this.setState({
      selectedStep: stepName,
      confirmRerun: true,
      pendingExtraBody: null,
      stepResults: clearedResults,
    });
  };

  confirmRerunStart = () => {
    const extraBody = this.state.pendingExtraBody || {};
    const selectedStep = this.state.selectedStep;
    this.setState({ confirmRerun: true }, () => {
      this.startAgent(extraBody, {
        forceRerun: true,
        fromStep: selectedStep,
        skipConfirmation: true,
      });
    });
  };

  cancelRerun = () => {
    this.setState({ confirmRerun: false, pendingExtraBody: null });
  };

  /* ────────── SSE event handlers ────────── */

  handleStepStart = (stepInfo) => {
    this.setState({ currentlyExecutingStep: stepInfo.step });
  };

  handleStepCompleted = (stepInfo) => {
    console.log('[AgentPanel] Step completed via SSE:', stepInfo.step, stepInfo.success);
    this.setState(prev => {
      const existing = prev.stepResults.find(r => r.step === stepInfo.step);
      if (existing) return null; // already tracked
      return {
        stepResults: [...prev.stepResults, { step: stepInfo.step, success: stepInfo.success }],
        currentlyExecutingStep: null,
      };
    });
    // Fire per-step callbacks immediately so parent UI refreshes in real-time 
    if (stepInfo.success) {
      this.notifyCheckpointCallbacks(stepInfo.step);
      // Trigger wallet refresh to show updated coin balance after step deduction
      const nc = this.props.notificationContext;
      if (nc?.notifyWalletRefresh) {
        console.log('[AgentPanel] Notifying wallet refresh after step completion');
        nc.notifyWalletRefresh();
      }
    }
  };

  handleCheckpointReached = async (checkpointInfo) => {
    console.log('[AgentPanel] Checkpoint reached:', checkpointInfo);
    this.clearTimer();

    // Trigger wallet refresh to show updated coin balance
    const nc = this.props.notificationContext;
    if (nc?.notifyWalletRefresh) {
      console.log('[AgentPanel] Notifying wallet refresh at checkpoint');
      nc.notifyWalletRefresh();
    }

    try {
      const response = await fetch(urlMap.getAgentExecution(checkpointInfo.executionId), {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (response.ok) {
        const execution = await response.json();
        const completedStepName = checkpointInfo.step || execution.currentStep;
        const agentSteps = this.getAgentSteps();
        const currentIndex = agentSteps.findIndex(s => s.name === completedStepName);
        const nextStepConfig = currentIndex >= 0 && currentIndex < agentSteps.length - 1
          ? agentSteps[currentIndex + 1] : null;

        this.notifyCheckpointCallbacks(completedStepName);

        this.setState({
          agentRunning: false, agentPaused: true, atCheckpoint: true,
          nextStep: nextStepConfig?.name, completedStep: completedStepName, canResume: true,
        });
      }
    } catch (error) {
      console.error('[AgentPanel] Failed to fetch execution state:', error);
      this.setState({
        agentRunning: false, agentPaused: true, atCheckpoint: true,
        completedStep: checkpointInfo.step, canResume: true, currentlyExecutingStep: null,
      });
    }
  };

  handleExecutionComplete = async (result) => {
    console.log('[AgentPanel] Execution completed:', result);
    this.clearTimer();
    this.setState({ currentlyExecutingStep: null });

    if (result.success) {
      try {
        const response = await fetch(urlMap.getAgentExecution(result.executionId), {
          headers: getAuthHeaders(), credentials: 'include',
        });
        if (response.ok) {
          const execution = await response.json();
          const executedStepNames = execution.stepsExecuted?.map(s => s.step) || [];

          if (executedStepNames.includes('SummarizeAndExtractStep')) this.props.onCharactersExtracted?.();
          if (executedStepNames.includes('SummarizeAndExtractStep')) this.props.onChaptersSummarized?.();
          if (
            executedStepNames.includes('AnalyzePlotStep') ||
            executedStepNames.includes('PlanRewriteStep') ||
            executedStepNames.includes('StructuralAndThematicStep')
          ) {
            this.props.onProjectMetadataChanged?.();
          }

          this.setState({ agentDone: true, agentRunning: false, agentError: null });
        }
      } catch (error) {
        console.error('[AgentPanel] Failed to fetch execution results:', error);
      }
    } else {
      this.setState({ agentRunning: false, agentError: result.error || 'Execution failed', agentDone: false });
    }
  };

  /* ────────── utilities ────────── */

  clearTimer = () => {
    if (this.agentTimer) { clearInterval(this.agentTimer); this.agentTimer = null; }
  };

  autoReset = () => {
    this.agentDoneTimeout = setTimeout(() => {
      this.setState({
        agentRunning: false, agentStep: 0, agentDone: false,
        executionId: null,
        // Keep stepResults so result-link icons persist on chips
      });
    }, 3000);
  };

  getAgentSteps = () => {
    return this.state.menuData?.steps || [];
  };

  getNextUnexecutedStep = () => {
    const steps = this.getEffectiveAgentSteps();
    if (!steps || steps.length === 0) return null;

    const completed = new Set(
      this.getStepResultsWithProjectData()
        .filter(r => r.success)
        .map(r => r.step)
    );

    const next = steps.find(step => step.enabled !== false && !completed.has(step.name));
    return next?.name || null;
  };

  handlePrimaryAction = () => {
    const nextStep = this.getNextUnexecutedStep();
    const hasExecutedSteps = this.getStepResultsWithProjectData().some(r => r.success);

    if (hasExecutedSteps && !nextStep && this.getPendingStructuralIssuesCount() > 0) {
      this.setState({
        agentError: 'Approve or dismiss all Structure & Themes issues before continuing to Rewrite Plan.',
        activeResultModal: 'plot',
        resultModalInitialTab: 'structure',
      });
      return;
    }

    if (hasExecutedSteps && nextStep) {
      this.setState({ selectedStep: nextStep }, () => {
        this.startAgent({}, { forceRerun: false, fromStep: nextStep, skipConfirmation: true });
      });
      return;
    }

    this.setState({ selectedStep: null }, () => this.startAgent({}, { forceRerun: false }));
  };

  isStepBlocked = (stepName) => {
    const { stepResults } = this.state;
    if (!stepResults) return false;
    return stepResults.some(r => r.step === stepName && !r.success && r.metadata?.blocked);
  };

  /* ────────── step result modals ────────── */

  handleViewStepResults = (stepName) => {
    const config = STEP_RESULT_CONFIG[stepName];
    if (config) {
      // Ensure project metadata is fresh before opening modal
      if (this.props.onProjectMetadataChanged && stepName === 'StructuralAndThematicStep') {
        this.props.onProjectMetadataChanged?.();
      }
      this.setState({
        activeResultModal: config.modalType,
        resultModalInitialTab: config.tab || 'plot',
      });
    }
  };

  handleCloseResultModal = () => {
    this.setState({ activeResultModal: null, resultModalInitialTab: 'plot' });
  };

  getViewableSteps = () => {
    return new Set(Object.keys(STEP_RESULT_CONFIG));
  };

  /**
   * Detect completed steps from project metadata so result-link icons
   * persist even when no recent agent execution is loaded.
   */
  getStepResultsWithProjectData = () => {
    const { stepResults } = this.state;
    const project = this.props.project;
    const meta = project?.metadata || {};

    // Steps already marked completed by execution data
    const known = new Set((stepResults || []).map(r => r.step));
    const merged = [...(stepResults || [])];

    // AnalyzePlotStep — project has plot data (plot is only set if summaries existed)
    if (!known.has('AnalyzePlotStep') && meta.plot) {
      merged.push({ step: 'AnalyzePlotStep', success: true });
    }
    // SummarizeAndExtractStep — if plot exists, summaries must have existed too
    if (!known.has('SummarizeAndExtractStep') && meta.plot) {
      merged.push({ step: 'SummarizeAndExtractStep', success: true });
    }
    // PlanRewriteStep — project has rewrite plan
    if (!known.has('PlanRewriteStep') && meta.rewritePlan) {
      merged.push({ step: 'PlanRewriteStep', success: true });
    }

    return merged;
  };

  /* ────────── render ────────── */

  render() {
    const {
      agentRunning, agentPaused, agentStep, agentDone, agentError,
      canResume, atCheckpoint, completedStep, executionId, menuLoading,
    } = this.state;

    const { sidePanel, onTogglePanel, project, charactersRefreshTrigger } = this.props;

    const rawAgentSteps = this.getAgentSteps();
    const agentSteps = this.getEffectiveAgentSteps();
    const pendingStructuralIssuesCount = this.getPendingStructuralIssuesCount();
    const stepsReady = !menuLoading && rawAgentSteps.length > 0;
    const hasExecutedSteps = this.getStepResultsWithProjectData().some(r => r.success);
    const nextUnexecutedStep = this.getNextUnexecutedStep();
    const startLabel = hasExecutedSteps && nextUnexecutedStep
      ? (nextUnexecutedStep === 'PlanRewriteStep' ? 'Continue to Rewrite Plan' : 'Continue')
      : 'Run';

    const getStepTitle = (stepName) => {
      const step = rawAgentSteps.find(s => s.name === stepName);
      return step?.title || stepName;
    };

    const waitingForStructureIssueApproval =
      completedStep === 'StructuralAndThematicStep' && pendingStructuralIssuesCount > 0;

    return (
      <div className="agent-panel-container">
        <div className="agent-panel-header">
          {/* Inline control buttons in header row */}
          {stepsReady && !atCheckpoint && !this.state.confirmRerun && (
            <AgentControlButtons
              agentRunning={agentRunning}
              agentPaused={agentPaused}
              agentDone={agentDone}
              agentError={agentError}
              canResume={canResume}
              canCancel={!!executionId}
              startLabel={startLabel}
              doneLabel={this.getNextUnexecutedStep() ? 'Continue' : 'Restart'}
              onStart={this.handlePrimaryAction}
              onPause={this.pauseAgent}
              onCancel={this.cancelAgent}
              onResume={this.resumeAgent}
              onStop={this.stopAgent}
              onReset={this.resetAndRun}
            />
          )}
        </div>

        <div className="agent-panel-body">
          {/* Loading steps from backend */}
          {menuLoading && (
            <div className="agent-panel" aria-live="polite">
              <div className="agent-row">
                <div className="agent-spinner" aria-hidden="true" />
                <div className="agent-text">Loading steps…</div>
              </div>
            </div>
          )}

          {!menuLoading && agentSteps.length === 0 && (
            <div className="agent-error">No steps configured for this project.</div>
          )}

          {/* Spinner while running */}
          {stepsReady && agentRunning && (
            <AgentStatusPanel
              agentDone={agentDone}
              agentSteps={agentSteps}
              agentStep={agentStep}
            />
          )}

          {/* Checkpoint dialog */}
          {stepsReady && atCheckpoint && (
            <div className="checkpoint-dialog">
              <div className="checkpoint-content">
                {(this.isStepBlocked(completedStep) || waitingForStructureIssueApproval) ? (
                  <>
                    <span className="checkpoint-icon" style={{ color: '#f59e0b' }}>🔒</span>
                    <span className="checkpoint-text">
                      <strong>{getStepTitle(completedStep)}</strong> is blocked — approve or dismiss issues in Structure & Themes before continuing.
                      {waitingForStructureIssueApproval ? ` (${pendingStructuralIssuesCount} pending)` : ''}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="checkpoint-icon">✓</span>
                    <span className="checkpoint-text">
                      <strong>{getStepTitle(completedStep)}</strong> complete
                    </span>
                  </>
                )}
              </div>
              <div className="checkpoint-actions">
                {(this.isStepBlocked(completedStep) || waitingForStructureIssueApproval) ? (
                  <>
                    <button
                      type="button"
                      className="checkpoint-btn checkpoint-btn--continue"
                      onClick={() => { this.setState({ activeResultModal: 'plot', resultModalInitialTab: 'structure' }); }}
                    >
                      Review Issues
                    </button>
                    <button type="button" className="checkpoint-btn checkpoint-btn--stop" onClick={this.stopAgent}>Stop</button>
                  </>
                ) : completedStep === 'PlanRewriteStep' && !this.state.nextStep ? (
                  <button type="button" className="checkpoint-btn checkpoint-btn--continue" onClick={() => { this.stopAgent(); this.props.onOpenRewritePlan?.(); }}>View Rewrite Plan</button>
                ) : (
                  <>
                    <button type="button" className="checkpoint-btn checkpoint-btn--continue" onClick={this.continueAgent}>Continue</button>
                    <button type="button" className="checkpoint-btn checkpoint-btn--stop" onClick={this.stopAgent}>Stop</button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Confirm re-run dialog */}
          {stepsReady && this.state.confirmRerun && (
            <div className="checkpoint-dialog">
              <div className="checkpoint-content">
                <span className="checkpoint-icon" style={{ color: '#f59e0b' }}>⚠</span>
                <span className="checkpoint-text">
                  {this.state.selectedStep
                    ? <>Regenerating from <strong>{getStepTitle(this.state.selectedStep)}</strong> will replace data for this step and all subsequent steps. Continue?</>
                    : <>Regenerating will replace <strong>all existing step data</strong>. Continue?</>
                  }
                </span>
              </div>
              <div className="checkpoint-actions">
                <button type="button" className="checkpoint-btn checkpoint-btn--continue" onClick={this.confirmRerunStart}>Continue</button>
                <button type="button" className="checkpoint-btn checkpoint-btn--stop" onClick={this.cancelRerun}>Cancel</button>
              </div>
            </div>
          )}

          {/* Steps chips */}
          {stepsReady && (
            <AgentStepsChips
              steps={agentSteps}
              currentStep={this.state.currentStep}
              currentlyExecutingStep={this.state.currentlyExecutingStep}
              isRunning={agentRunning}
              isPaused={agentPaused}
              onRerunFromStep={this.handleRerunFromStep}
              stepResults={this.getStepResultsWithProjectData()}
              onViewResults={this.handleViewStepResults}
              viewableSteps={this.getViewableSteps()}
            />
          )}

          {/* Logs */}
          <AgentLogs
            logs={this.state.agentLogs}
            visible={!!executionId}
            isExpanded={this.state.logsExpanded}
            onToggleExpanded={() => this.setState(s => ({ logsExpanded: !s.logsExpanded }))}
            onClearLogs={() => this.setState({ agentLogs: [] })}
          />
        </div>

        {/* Step result modal */}
        <PlotModal
          isOpen={this.state.activeResultModal === 'plot'}
          onClose={this.handleCloseResultModal}
          project={project}
          refreshTrigger={charactersRefreshTrigger}
          initialTab={this.state.resultModalInitialTab}
          onTriggerAgentStep={this.triggerRunFromStep}
          onRefreshProject={this.props.onProjectMetadataChanged}
          onStructuralIssuesUpdated={this.handleStructuralIssuesUpdated}
          onIssuesCountChanged={this.props.onIssuesCountChanged}
        />
      </div>
    );
  }
}

// Wrapper component to provide NotificationContext to AgentPanel
export default function AgentPanel(props) {
  const notificationContext = React.useContext(NotificationContext);
  return <AgentPanelBase {...props} notificationContext={notificationContext} />;
}
